import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule, Validators, ReactiveFormsModule
} from '@angular/forms';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { CompanyOnboardingRequestView } from '../../../../core/models/company-onboarding-request-view.model';
import { CompanyProfileBulkView } from '../../../../core/models/company-profile-bulk-view.model';
import { CompanyAdminService } from '../../../../core/services/api-services/company-admin.service';
import { CompanyService } from '../../../../core/services/api-services/company.service';
import { PortalAdminService } from '../../../../core/services/api-services/portal-admin.service';
import { AuthService } from '../../../../core/services/client-services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../../shared/components/breadcrumb/service/breadcrumb.service';
import { PageEvent, PaginatorComponent } from "../../../../shared/components/paginator/paginator.component";
import { FilterPanelComponent } from '../../../../shared/components/filter-panel/filter-panel.component';
import { FilterField, FilterResult } from '../../../../shared/components/filter-panel/model/filter-field.model';

declare var bootstrap: any;

@Component({
  selector: 'app-employers-and-requests',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PaginatorComponent, BreadcrumbComponent, FilterPanelComponent],
  templateUrl: './employers-and-requests.component.html',
  styleUrl: './employers-and-requests.component.css'
})
export class EmployersAndRequestsComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  // ───────────────────────────────────────────────────────────────
  // ⬛ Component State and Data
  // ───────────────────────────────────────────────────────────────
  @ViewChild('employerFormOffcanvasEl') offcanvasElementRef!: ElementRef;

  @ViewChild(FilterPanelComponent) filterPanel!: FilterPanelComponent;

  employers: CompanyProfileBulkView[] = [];
  filteredEmployers: CompanyProfileBulkView[] = [];

  pagedEmployers: any[] = [];
  pagedOnboardingRequests: any[] = [];

  EmployersPage = 1;
  OnboardingRequestsPage = 1;
  EmployersPageSize = 10;
  OnboardingRequestsPageSize = 10;
  EmployersPageSizeOptions = [5, 10, 25, 50];
  OnboardingRequestsPageSizeOptions = [5, 10, 25, 50];

  // Employers server-side pagination
  totalEmployers: number = 0;
  employersCurrentPage: number = 1;
  employersPageSize: number = 10;

  // Onboarding requests server-side pagination
  totalOnboardingRequests: number = 0;
  onboardingCurrentPage: number = 1;
  onboardingPageSize: number = 10;


  onboardingRequests: CompanyOnboardingRequestView[] = [];
  filteredOnboardingRequests: CompanyOnboardingRequestView[] = [];

  isEmpLoading = false;
  isObrLoading = false;
  empError = '';
  obrError = '';

  searchText: string = '';
  searhchOnboardingText: string = '';
  employerForm!: FormGroup;
  mode: 'add' | 'view' | 'edit' = 'add';
  selectedRequest: any = null;
  obrCopiedField: string | null = null;
  private obrCopyTimer: any = null;

  selectedCompanyProfile: any = null;
  profileLoading = false;
  profileError = '';

  dataSubscriber$!: Subscription;

  // ─── ViewChild ───────────────────────────────────────────────
  @ViewChild('confirmEmployerTypeModal') confirmEmployerTypeModalEl!: ElementRef;
  private confirmEmployerTypeModalInstance: any;

  @ViewChild('ewwBlockModal') ewwBlockModalRef!: ElementRef;
  @ViewChild('ewwUnblockModal') ewwUnblockModalRef!: ElementRef;
  private ewwBlockModalInstance: any = null;
  private ewwUnblockModalInstance: any = null;

  // ─── State ───────────────────────────────────────────────────
  pendingIsPayDay: boolean = false;
  pendingIsPayRoll: boolean = false;
  currentCombinationLabel: string = '';
  pendingCombinationLabel: string = '';
  isUpdatingEmployerType = false;

  // ─── EWW Block State ─────────────────────────────────────────
  isEwwBlocked: boolean = false;
  isEwwBlockSaving: boolean = false;
  ewwBlockReason: string = '';

  protected readonly TYPE_MAP: Record<number, string> = {
    1: 'CIXOR Payday',
    2: 'CIXOR Payroll'
  };

  filterEmployerTypeId: number | string = '';

  // =============== Server filter Props =============== //

  filterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'CompanyId',
      options: [
        {
          value: 'CompnayId',
          label: 'Compnay ID',
          field: {
            type: 'text',
            controlName: 'CompanyId',            // key sent to backend
            placeholder: 'Enter Company ID',
            hint: 'Example: 9412265199 (10 digits)',
            validators: [Validators.required, Validators.pattern(/^\d{10}$/), Validators.minLength(10), Validators.maxLength(10)],
          },
        },
        {
          value: 'directorNic',
          label: 'Director NIC',
          field: {
            type: 'text',
            controlName: 'DirectorNic',            // key sent to backend
            placeholder: 'Enter Director NIC',
            hint: 'Example: 98XXXXXXXV / 1998XXXXXXXX (9 or 12 digits)',
            validators: [Validators.required, Validators.pattern(/^([0-9]{9}[vVxX]|[0-9]{12})$/), Validators.minLength(9), Validators.maxLength(12)],
          },
        }
      ],
    },
  ];

  // =================================================== //

  // ───────────────────────────────────────────────────────────────
  // ⬛ Constructor
  // ───────────────────────────────────────────────────────────────
  constructor(
    private toaster: ToastrService,
    private portalAdminService: PortalAdminService,
    private companyService: CompanyService,
    private fb: FormBuilder,
    public authService: AuthService,
    private companyAdminService: CompanyAdminService,
    private sanitizer: DomSanitizer,
    private breadcrumbService: BreadcrumbService
  ) {
    this.dataSubscriber$ = new Subscription();
  }


  // ───────────────────────────────────────────────────────────────
  // ⬛ Lifecycle Hook
  // ───────────────────────────────────────────────────────────────
  ngOnInit(): void {
    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Client', icon: 'bi bi-people' },
      { label: 'Employer', icon: 'bi bi-building' }
    ]);

    this.initAddemployerForm();
    this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId);
    this.fetchOnboardingRequests();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Form Initialization and Validation
  // ───────────────────────────────────────────────────────────────
  initAddemployerForm(): void {
    this.employerForm = this.fb.group({
      companyId: [null],
      companyName: [''],
      cixorReferenceNumber: [null],
      companyTradingName: [''],
      legalType: [''],
      authContactFirstName: [''],
      authContactLastName: [''],
      companyLogoBase64: [null],
      mainIndustryType: [''],
      noOfEmployeesType: [''],
      annualTurnOverType: [''],
      monthlySalaryBillType: [''],
      maximumAllowedSalaryAdvance: [''],
      longitude: [null],
      latitude: [null],
      placeId: [''],
      companyRegNo: [''],
      registeredAddress: [''],
      operatingAddress: [''],
      country: [''],
      landLineNumber: [''],
      bankId: [null],
      bankName: [''],
      bankBranchId: [null],
      accountNumber: [''],
      branchName: [''],
      branchAddress: [''],
      mobileNumber: [''],
      email: [''],
      regAddress_MobileNumber: [''],
      regAddress_Email: [''],
      businessAddress_MobileNumber: [''],
      businessAddress_Email: [''],
      companySecrataryDetails: [''],
      companyAuditorDetails: [''],
      dateOfIncorporation: [null],
      salaryDeduction: [null],
      website: [null],
      salaryRangeFrom: [null],
      salaryRangeTo: [null],
      directorsDTOs: this.fb.array([]),
      shareholderDTOs: this.fb.array([]),
      leaveDTOs: this.fb.control(null)
    });
  }

  passwordsMatchValidator(formGroup: FormGroup): null | object {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ API Calls
  // ───────────────────────────────────────────────────────────────

  onEmployerTypeFilterChange(): void {
    this.employersCurrentPage = 1;
    this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId);
  }

  // fetchEmployers(): void {
  //   this.isLoading = true;
  //   this.dataSubscriber$.add(
  //     this.companyService.getExistingCompanies().subscribe({
  //       next: (response) => {
  //         if (response.success) {
  //           this.employers = response.data;
  //           this.filteredEmployers = [...this.employers]; // initialize filtered list
  //           this.pagedEmployers = this.filteredEmployers.slice(0, 10);  // first page
  //           this.onEmployerPageChange(this.filteredEmployers.slice(0, 10));
  //         } else {
  //           this.error = 'Failed to load Employers.';
  //           this.filteredEmployers = [];
  //           this.pagedEmployers = [];
  //         }
  //         this.isLoading = false;
  //       },
  //       error: (err) => {
  //         this.error = 'API error: ' + err.message;
  //         this.isLoading = false;
  //         this.filteredEmployers = [];
  //         this.pagedEmployers = [];
  //         this.isLoading = false;
  //       }
  //     })
  //   );
  // }
  fetchEmployers(page: number, pageSize: number, employerTypeId: number | string, companyId?: string, directorNic?: string): void {
    this.isEmpLoading = true;
    this.empError = '';
    this.dataSubscriber$.add(
      this.companyService.getExistingCompaniesPaginated(page, pageSize, employerTypeId, companyId, directorNic).subscribe({
        next: (response) => {
          if (response.success) {
            this.employers = response.data;
            this.filteredEmployers = [...this.employers];
            this.pagedEmployers = [...this.employers];
            this.totalEmployers = response.totalCount;
            this.employersCurrentPage = response.pageNumber;
            this.employersPageSize = response.pageSize;
          } else {
            this.filteredEmployers = [];
            this.pagedEmployers = [];
            this.empError = 'Failed to load Employers.';
          }
          this.isEmpLoading = false;
        },
        error: (err) => {
          this.filteredEmployers = [];
          this.pagedEmployers = [];
          this.empError = 'API error: ' + err.message;
          this.isEmpLoading = false;
        }
      })
    );
  }

  // fetchOnboardingRequests(): void {
  //   this.isLoading = true;
  //   this.dataSubscriber$.add(
  //     this.companyService.getAllCompanyOnboardingRequests().subscribe({
  //       next: (response) => {
  //         if (response.success) {
  //           // console.log(response.data);
  //           this.onboardingRequests = response.data;
  //           this.filteredOnboardingRequests = [...this.onboardingRequests]; // initialize filtered list
  //           // console.log('RQ :', this.filteredOnboardingRequests);

  //         } else {
  //           this.error = 'Failed to load Employers.';
  //         }
  //         this.isLoading = false;
  //       },
  //       error: (err) => {
  //         this.error = 'API error: ' + err.message;
  //         this.isLoading = false;
  //       }
  //     })
  //   );
  // }
  fetchOnboardingRequests(): void {
    this.isObrLoading = true;
    this.dataSubscriber$.add(
      this.companyService.getAllCompanyOnboardingRequestsPaginated(this.onboardingCurrentPage, this.onboardingPageSize).subscribe({
        next: (response) => {
          if (response.success) {
            this.onboardingRequests = response.data;
            this.filteredOnboardingRequests = [...this.onboardingRequests];
            this.pagedOnboardingRequests = [...this.onboardingRequests];
            this.totalOnboardingRequests = response.totalCount;
            this.onboardingCurrentPage = response.pageNumber;
            this.onboardingPageSize = response.pageSize;
          } else {
            this.filteredOnboardingRequests = [];
            this.pagedOnboardingRequests = [];
          }
          this.isObrLoading = false;
        },
        error: (err) => {
          this.filteredOnboardingRequests = [];
          this.pagedOnboardingRequests = [];
          this.obrError = 'API error: ' + err.message;
          this.isObrLoading = false;
        }
      })
    );
  }

  formatPhones(r: CompanyOnboardingRequestView) {
    return [r.landLineNumber, r.mobileNumber01, r.mobileNumber02]
      .filter((v): v is string => !!v && v.trim() !== '')
      .join(' / ') || '-';
  }

  copyContact(value: string | null, field: string): void {
    if (!value) return;
    navigator.clipboard.writeText(value).catch(() => {});
    this.obrCopiedField = field;
    if (this.obrCopyTimer) clearTimeout(this.obrCopyTimer);
    this.obrCopyTimer = setTimeout(() => { this.obrCopiedField = null; }, 1800);
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Form Mode Handlers (Add, View, Edit)
  // ───────────────────────────────────────────────────────────────
  openAddemployerForm(): void {
    this.mode = 'add';
    this.selectedRequest = null;
    this.employerForm.enable();
    this.employerForm.reset();
  }

  viewOnboardingRequest(request: any): void {
    this.mode = 'view';
    this.selectedRequest = request;
    // console.log(this.selectedRequest)
    this.employerForm.patchValue(request);
    this.employerForm.disable();
  }

  openEmployerProfile(companyId: number): void {
    if (!companyId) { this.profileError = 'Invalid company id.'; return; }
    this.profileLoading = true;
    this.profileError = '';
    this.selectedCompanyProfile = null;

    this.dataSubscriber$.add(
      this.companyService.getCompanyProfile(companyId)
        .pipe(finalize(() => this.profileLoading = false))
        .subscribe({
          next: (resp) => {
            if (resp?.success && resp.data) {
              this.selectedCompanyProfile = resp.data; // full profile payload
              this.syncEmployerType();
              this.syncEwwBlockState();
            } else {
              this.profileError = resp?.message || 'Failed to load company profile.';
            }
          },
          error: (err) => {
            this.profileError = 'API error: ' + (err?.message || 'Unknown error');
          }
        })
    );
  }

  enterEditMode(): void {
    this.mode = 'edit';
    this.employerForm.enable();

    // Disable non-editable fields in edit mode
    this.employerForm.controls['userName'].disable();
    this.employerForm.controls['employeeId'].disable();
    this.employerForm.controls['email'].disable();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Field Visibility & Enabled State Logic
  // ───────────────────────────────────────────────────────────────
  isDisabled(field: string): boolean {
    if (this.mode === 'add') return false;
    if (this.mode === 'view') return true;

    if (this.mode === 'edit') {
      const editableFields = [
        'firstName',
        'lastName',
        'email',
        'mobileNumber',
        'department',
        'designationId'
      ];
      return !editableFields.includes(field);
    }

    return true;
  }

  isFieldVisible(field: string): boolean {
    if (this.mode === 'view' || this.mode === 'edit') {
      const visibleFields = [
        'firstName',
        'lastName',
        'userName',
        'employeeId',
        'email',
        'mobileNumber',
        'department',
        'designationId'
      ];
      return visibleFields.includes(field);
    }

    return true; // Show all fields in 'add' mode
  }

  // ─── Called when profile loads ─────────────────────────────
  // Inside your openEmployerProfile() or wherever you set selectedCompanyProfile,
  // initialize pendingEmployerTypeId from the loaded data:

  private syncEmployerType(): void {
    const typeId = this.selectedCompanyProfile?.basicRegistration?.employerTypeId ?? null;
    const isEligibleForPayRoll = this.selectedCompanyProfile?.basicRegistration?.isEligibleForPayRoll ?? false;
    this.pendingIsPayDay = typeId === 1;
    this.pendingIsPayRoll = typeId === 2 || (typeId === 1 && !!isEligibleForPayRoll);
  }

  private getCombinationLabel(isPayDay: boolean, isPayRoll: boolean): string {
    if (isPayDay && isPayRoll) return 'CIXOR PayDay + PayRoll';
    if (isPayDay) return 'CIXOR PayDay';
    if (isPayRoll) return 'CIXOR PayRoll';
    return 'None';
  }

  private syncEwwBlockState(): void {
    this.isEwwBlocked = this.selectedCompanyProfile?.basicRegistration?.isBlockEWW ?? false;
  }


  // ─── Called on checkbox card click ─────────────────────────
  toggleEmployerTypeCheckbox(type: 'payday' | 'payroll', p: any): void {
    const savedTypeId = p.basicRegistration?.employerTypeId ?? null;
    const savedIsEligibleForPayRoll = p.basicRegistration?.isEligibleForPayRoll ?? false;
    const savedIsPayDay = savedTypeId === 1;
    const savedIsPayRoll = savedTypeId === 2 || (savedTypeId === 1 && !!savedIsEligibleForPayRoll);

    let newIsPayDay = this.pendingIsPayDay;
    let newIsPayRoll = this.pendingIsPayRoll;

    if (type === 'payday') {
      newIsPayDay = !this.pendingIsPayDay;
    } else {
      newIsPayRoll = !this.pendingIsPayRoll;
    }

    // At least one must be selected
    if (!newIsPayDay && !newIsPayRoll) {
      this.toaster.warning('At least one employer type must be selected.', 'Warning');
      return;
    }

    // No change from saved state — update pending UI but don't open modal
    if (newIsPayDay === savedIsPayDay && newIsPayRoll === savedIsPayRoll) {
      this.pendingIsPayDay = newIsPayDay;
      this.pendingIsPayRoll = newIsPayRoll;
      return;
    }

    this.pendingIsPayDay = newIsPayDay;
    this.pendingIsPayRoll = newIsPayRoll;
    this.currentCombinationLabel = this.getCombinationLabel(savedIsPayDay, savedIsPayRoll);
    this.pendingCombinationLabel = this.getCombinationLabel(newIsPayDay, newIsPayRoll);

    // Open confirmation modal
    this.confirmEmployerTypeModalInstance =
      new bootstrap.Modal(this.confirmEmployerTypeModalEl.nativeElement);
    this.confirmEmployerTypeModalInstance.show();
  }

  cancelEmployerTypeChange(): void {
    // Revert checkboxes to saved state
    const currentTypeId = this.selectedCompanyProfile?.basicRegistration?.employerTypeId ?? null;
    const isEligibleForPayRoll = this.selectedCompanyProfile?.basicRegistration?.isEligibleForPayRoll ?? false;
    this.pendingIsPayDay = currentTypeId === 1;
    this.pendingIsPayRoll = currentTypeId === 2 || (currentTypeId === 1 && !!isEligibleForPayRoll);
    this.confirmEmployerTypeModalInstance?.hide();
  }

  confirmEmployerTypeChange(): void {
    if (!this.selectedCompanyProfile) return;

    const companyId = this.selectedCompanyProfile.basicRegistration?.companyId;
    this.isUpdatingEmployerType = true;

    // Derive request values from pending checkbox state
    let employerTypeId: number;
    let isEligibleForPayRoll: boolean;
    let isPayRollGrantedByAdmin: boolean;

    if (this.pendingIsPayDay && this.pendingIsPayRoll) {
      employerTypeId = 1;
      isEligibleForPayRoll = true;
      isPayRollGrantedByAdmin = true;
    } else if (this.pendingIsPayDay) {
      employerTypeId = 1;
      isEligibleForPayRoll = false;
      isPayRollGrantedByAdmin = true; // Keep payroll granted if switching to payday-only, as they still have access to payroll features until/unless admin revokes
    } else {
      employerTypeId = 2;
      isEligibleForPayRoll = true;
      isPayRollGrantedByAdmin = true;
    }

    this.dataSubscriber$.add(
      this.companyService.updateEmployerType({
        companyId,
        employerTypeId,
        isEligibleForPayRoll,
        isPayRollGrantedByAdmin
      }).subscribe({
        next: (response) => {
          if (response.success) {
            this.confirmEmployerTypeModalInstance?.hide();

            // Update locally so next toggle computes diff correctly
            this.selectedCompanyProfile.basicRegistration.employerTypeId = employerTypeId;
            this.selectedCompanyProfile.basicRegistration.isEligibleForPayRoll = isEligibleForPayRoll;
            this.selectedCompanyProfile.basicRegistration.isPayRollGrantedByAdmin = isPayRollGrantedByAdmin;

            this.toaster.success('Employer features updated successfully.', 'Updated');
          } else {
            this.toaster.error('Failed to update employer features.', 'Error');
            this.cancelEmployerTypeChange();
          }
        },
        error: () => {
          this.toaster.error('Failed to update employer features.', 'Error');
          this.cancelEmployerTypeChange();
        },
        complete: () => {
          this.isUpdatingEmployerType = false;
        }
      })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Close Off Canvas
  // ───────────────────────────────────────────────────────────────
  closeOffcanvas(): void {
    const offcanvasEl = this.offcanvasElementRef?.nativeElement;
    if (offcanvasEl) {
      const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl) || new bootstrap.Offcanvas(offcanvasEl);
      bsOffcanvas.hide();
    }
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Form Actions (Submit/Add/Save)
  // ───────────────────────────────────────────────────────────────
  submitForm(): void {
    if (this.employerForm.invalid) {
      this.employerForm.markAllAsTouched();
      return;
    }

    const formData = {
      portalAdminId: 0,
      employeeId: this.employerForm.value.employeeId,
      designationId: this.employerForm.value.designationId,
      firstName: this.employerForm.value.firstName,
      lastName: this.employerForm.value.lastName,
      userName: this.employerForm.value.userName,
      mobileNumber: this.employerForm.value.mobileNumber,
      email: this.employerForm.value.email,
      passwordHash: this.employerForm.value.password,
      passwordSalt: '',
      pinHash: '1234567',
      pinSalt: '',
      isActive: true,
      createdDate: new Date().toISOString(),
      profilePic: null,
      invalidLogInAttempts: 0,
      isVerified: true
    };

    this.dataSubscriber$.add(
      this.portalAdminService.createPortalAdmin(formData).subscribe({
        next: (response) => {
          this.toaster.success('Employer added successfully.', 'Added');
          this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId); // Refresh employer list
          this.closeOffcanvas();
        },
        error: (err) => {
          this.toaster.error('Failed to add employer.', 'Error');
        }
      })
    );
  }


  saveChanges(): void {
    if (this.employerForm.invalid || !this.selectedRequest) {
      this.employerForm.markAllAsTouched();
      return;
    }

    const updatedData = {
      portalAdminId: this.selectedRequest.portalAdminId,
      designationId: this.employerForm.value.designationId,
      firstName: this.employerForm.value.firstName,
      lastName: this.employerForm.value.lastName,
      mobileNumber: this.employerForm.value.mobileNumber,
      isActive: true
    };

    this.dataSubscriber$.add(
      this.portalAdminService
        .updatePortalAdmin(this.selectedRequest.portalAdminId, updatedData)
        .subscribe({
          next: (response) => {
            this.toaster.success('Employer updated successfully.', 'Updated');
            this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId); // Refresh list
            this.closeOffcanvas();
          },
          error: (err) => {
            this.toaster.error('Failed to update employer.', 'Error');
          }
        })
    );
  }


  // ───────────────────────────────────────────────────────────────
  // ⬛ Search Functionality
  // ───────────────────────────────────────────────────────────────
  performSearch(): void {
    const search = this.searchText.trim().toLowerCase();
    if (!search) {
      this.filteredEmployers = [...this.employers]; // Reset if empty
      this.resetEmployersPagination();
      return;
    }

    this.filteredEmployers = this.employers.filter(employer =>
      Object.values(employer).some(val =>
        String(val).toLowerCase().includes(search)
      )
    );
    this.resetEmployersPagination();
  }

  performOnboardingRequestSearch(): void {
    const search = this.searhchOnboardingText.trim().toLowerCase();
    if (!search) {
      this.filteredOnboardingRequests = [...this.onboardingRequests]; // Reset if empty
      this.resetOnboardingRequestsPagination();
      return;
    }

    this.filteredOnboardingRequests = this.onboardingRequests.filter(request =>
      Object.values(request).some(val =>
        String(val).toLowerCase().includes(search)
      )
    );
    this.resetOnboardingRequestsPagination();
  }


  approveSelectedRequest(grantAdminAccess: boolean = true): void {
    if (!this.selectedRequest?.companyId) {
      this.toaster.warning('Please select a request first.', 'Warning');
      return;
    }

    const companyId = this.selectedRequest.companyId;
    const approvePayload = {
      companyId: this.selectedRequest.companyId as number,
      authorizedBy: String(this.authService.getUser()?.portalAdminId ?? 'system-admin'),
      grantAdminAccess: grantAdminAccess,
      approve: true,
      onlyIfPending: true
    };

    const initialContactAdminPayload = {
      companyId: Number(this.selectedRequest.companyId),
      companyName: this.selectedRequest.companyName,
      email: String(this.selectedRequest.email ?? ''),
      firstName: String(this.selectedRequest.initialContactFirstName ?? ''),
      lastName: String(this.selectedRequest.initialContactLastName ?? ''),
      mobileNumber: this.selectedRequest.mobileNumber01
    };
    this.isObrLoading = true;

    this.dataSubscriber$.add(
      this.companyService.authorizeCompanyOnboardingRequestAsync(companyId, approvePayload).pipe(
        switchMap(res => {
          // console.log('res -', res);
          if (!res?.success) {
            this.toaster.error('Failed to approve request.', 'Failed');
            // stop chain here
            return of<{ success: boolean } | null>(null);
          }
          this.toaster.success('Onboarding request approved.', 'Approved');
          this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId);
          this.fetchOnboardingRequests();
          return this.companyAdminService.OnboardCompanyInitialContactAdminAndNotifyAsync(companyId, initialContactAdminPayload);
        }),
        finalize(() => this.isObrLoading = false),
        catchError(err => {
          this.toaster.error('API error while approving/sending credentials.', 'Error');
          return of(null);
        })
      ).subscribe(sendRes => {
        if (sendRes === null) return;

        if (sendRes.success) {
          this.toaster.success('Credentials emailed to initial contact.', 'Email Sent');
          this.closeOffcanvas();
          this.onboardingRequests = this.onboardingRequests.filter(r => r.companyId !== companyId);
          this.filteredOnboardingRequests = this.filteredOnboardingRequests.filter(r => r.companyId !== companyId);
          this.selectedRequest = null;
        } else {
          this.toaster.warning('Approved, but failed to send credentials.', 'Email Sending failed');
          this.closeOffcanvas();

        }
      })
    );
  }

  /** Reject selected onboarding request */
  rejectSelectedRequest(): void {
    if (!this.selectedRequest?.companyId) {
      this.toaster.warning('Please select a request first.', 'Warning');
      return;
    }

    const companyId = this.selectedRequest.companyId;
    const payload = {
      companyId: this.selectedRequest.companyId as number,
      authorizedBy: String(this.authService.getUser()?.portalAdminId ?? 'system-admin'),
      grantAdminAccess: false,
      approve: false,
      onlyIfPending: true
    };


    this.isObrLoading = true;

    this.dataSubscriber$.add(
      this.companyService.authorizeCompanyOnboardingRequestAsync(companyId, payload).subscribe({
        next: (res) => {
          if (res.success) {
            this.toaster.success('Onboarding request rejected.', 'Rejected');
            this.onboardingRequests = this.onboardingRequests.filter(r => r.companyId !== companyId);
            this.filteredOnboardingRequests = this.filteredOnboardingRequests.filter(r => r.companyId !== companyId);
            this.selectedRequest = null;
            this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId);
            this.fetchOnboardingRequests();
            this.closeOffcanvas();
          } else {
            this.toaster.error('Failed to reject request.', 'Failed');
          }
          this.isObrLoading = false;
        },
        error: (err) => {
          this.toaster.error('API error while rejecting request.', 'Error');
          this.isObrLoading = false;
        }
      })
    );
  }

  /* ===== Review helpers (payload, dispatchers, patchers) ===== */

  private reviewerIdNumber(): number {
    const id = this.authService?.getUser()?.portalAdminId;
    return (typeof id === 'number' ? id : Number(id)) || 0;
  }
  private nowIso(): string {
    return new Date().toISOString();
  }
  private getCurrentCompanyId(): number | null {
    return this.selectedCompanyProfile?.basicRegistration?.companyId ?? null;
  }

  private buildReviewBody(id: number, approve: boolean, remark?: string) {
    return {
      Id: id,
      IsApproved: approve,
      ReviewerRemark: remark ?? null,
      ReviewedBy: this.reviewerIdNumber(),
    } as const;
  }

  private postSectionReview(
    sectionKey: 'contactDetails' | 'financialSnapshot' | 'authorizedContact' | 'secretary' | 'auditor',
    companyId: number,
    body: { Id: number; IsApproved: boolean; ReviewerRemark: string | null; ReviewedBy: number; }
  ) {
    switch (sectionKey) {
      case 'contactDetails': return this.companyService.reviewContactDetails(companyId, body);
      case 'financialSnapshot': return this.companyService.reviewFinancialSnapshot(companyId, body);
      case 'authorizedContact': return this.companyService.reviewAuthorizedContact(companyId, body);
      case 'secretary': return this.companyService.reviewSecretary(companyId, body);
      case 'auditor': return this.companyService.reviewAuditor(companyId, body);
    }
  }

  private postItemReview(
    listKey: 'documents' | 'bankAccounts' | 'directors' | 'shareholders',
    companyId: number,
    body: { Id: number; IsApproved: boolean; ReviewerRemark: string | null; ReviewedBy: number; }
  ) {
    switch (listKey) {
      case 'documents': return this.companyService.reviewDocuments(companyId, body);
      case 'bankAccounts': return this.companyService.reviewBankAccounts(companyId, body);
      case 'directors': return this.companyService.reviewDirectors(companyId, body);
      case 'shareholders': return this.companyService.reviewShareholders(companyId, body);
    }
  }

  /* Local patch helpers */
  private patchLocalSection(
    sectionKey: 'contactDetails' | 'financialSnapshot' | 'authorizedContact' | 'secretary' | 'auditor',
    patch: Partial<any>
  ) {
    if (!this.selectedCompanyProfile) return;
    this.selectedCompanyProfile = {
      ...this.selectedCompanyProfile,
      [sectionKey]: { ...this.selectedCompanyProfile[sectionKey], ...patch }
    };
  }
  private patchLocalItem(
    listKey: 'documents' | 'bankAccounts' | 'directors' | 'shareholders',
    itemId: number,
    patch: Partial<any>
  ) {
    if (!this.selectedCompanyProfile?.[listKey]) return;
    const list = this.selectedCompanyProfile[listKey] as any[];
    const idx = list.findIndex(x => x?.id === itemId);
    if (idx > -1) {
      const updated = { ...list[idx], ...patch };
      const newList = [...list];
      newList[idx] = updated;
      this.selectedCompanyProfile = { ...this.selectedCompanyProfile, [listKey]: newList };
    }
  }

  /* ===== Actions wired from the template ===== */

  /** Approve/Reject a single section object (server call + local patch) */
  reviewSection(
    sectionKey: 'contactDetails' | 'financialSnapshot' | 'authorizedContact' | 'secretary' | 'auditor',
    sectionObj: any,
    approve: boolean,
    remark?: string
  ) {
    const companyId = this.getCurrentCompanyId();
    if (!companyId || !sectionObj?.id) {
      console.warn('[reviewSection] Missing company/section id', { companyId, sectionObj });
      return;
    }

    const body = this.buildReviewBody(sectionObj.id, approve, remark);

    const superAdminPayload = {
      companyId: this.selectedCompanyProfile?.basicRegistration?.companyId,
      companyName: this.selectedCompanyProfile?.basicRegistration?.companyName,
      email: this.selectedCompanyProfile?.authorizedContact?.emailAddress,
      firstName: this.selectedCompanyProfile?.authorizedContact?.firstName,
      lastName: this.selectedCompanyProfile?.authorizedContact?.lastName,
      mobileNumber: this.selectedCompanyProfile?.authorizedContact?.contactNumber01
    };

    this.dataSubscriber$.add(
      this.postSectionReview(sectionKey, companyId, body)
        ?.pipe(finalize(() => { }))
        .subscribe({
          next: (res: any) => {
            if (res?.success) {
              this.patchLocalSection(sectionKey, {
                isReviewed: true,
                isApproved: approve,
                reviewedOn: this.nowIso(),
                reviewedBy: this.reviewerIdNumber(),
                reviewerRemark: remark ?? null
              });
              if (approve) {
                this.companyAdminService
                .UpdateSuperAdminAndNotifyAsync(companyId, superAdminPayload)
                .subscribe();
              }
              this.toaster.success(
                `${this.prettySection(sectionKey)} ${approve ? 'approved' : 'rejected'}.`,
                approve ? 'Approved' : 'Rejected'
              );
            } else {
              this.toaster.error(`Failed to review ${this.prettySection(sectionKey).toLowerCase()}.`, 'Error');
            }
          },
          error: (err) => {
            console.error('[reviewSection] API error', err);
            this.toaster.error(`API error while reviewing ${this.prettySection(sectionKey).toLowerCase()}.`, 'Error');
          }
        })
    );
  }

  /** Approve/Reject a single list item (server call + local patch) */
  /** Friendly label for toast sentences */
  private actionLabelFromType(actionType?: 'ADD' | 'EDIT' | 'DELETE'): 'Add' | 'Edit' | 'Delete' {
    switch (actionType) {
      case 'EDIT': return 'Edit';
      case 'DELETE': return 'Delete';
      case 'ADD':
      default: return 'Add';
    }
  }

  /** A small, friendly name for the item in messages */
  /** A small, friendly name for the item in messages */
  private itemDisplayName(
    listKey: 'documents' | 'bankAccounts' | 'directors' | 'shareholders',
    item: any
  ): string {
    if (!item) return 'Item';

    let candidate: string | undefined;

    switch (listKey) {
      case 'documents':
        candidate = (item?.documentTitle ?? (item?.id != null ? `#${item.id}` : undefined));
        break;

      case 'bankAccounts':
        candidate = item?.accountNumber
          ? `Account ${item.accountNumber}`
          : (item?.id != null ? `#${item.id}` : undefined);
        break;

      case 'directors':
        candidate = (item?.directorName ?? (item?.id != null ? `#${item.id}` : undefined));
        break;

      case 'shareholders':
        candidate = (item?.shareholderName ?? (item?.id != null ? `#${item.id}` : undefined));
        break;

      default:
        candidate = undefined;
    }

    const trimmed = (candidate ?? '').trim();
    if (trimmed) return trimmed;

    // Type-specific fallback label
    switch (listKey) {
      case 'bankAccounts': return 'Account';
      case 'directors': return 'Director';
      case 'shareholders': return 'Shareholder';
      case 'documents':
      default: return 'Item';
    }
  }

  /** Builds a human-readable toast based on exact actionType (ADD/EDIT/DELETE) */
  private humanizeDecision(
    entity: string,                                       // "Document", "Bank account", ...
    name: string,                                         // "Account 1234"
    action: 'ADD' | 'EDIT' | 'DELETE',                        // <-- use as-is
    approve: boolean,
    remark?: string | null
  ): { title: 'Approved' | 'Rejected', message: string } {
    const subject = `${entity.toLowerCase()} “${name}”`;
    let core: string;

    if (approve) {
      if (action === 'ADD') core = `Approved new ${subject}.`;
      else if (action === 'EDIT') core = `Approved changes to ${subject}.`;
      else core = `Approved removal of ${subject}.`;
    } else {
      if (action === 'ADD') core = `Successfully Rejected new ${subject}.`;
      else if (action === 'EDIT') core = `Successfully Rejected changes to ${subject}.`;
      else core = `Successfully Rejected removal of ${subject}.`;
    }

    const tail = remark ? ` — Note: ${remark}` : '';
    return { title: approve ? 'Approved' : 'Rejected', message: core + tail };
  }

  /** Remove an item from a list locally */
  private removeLocalItem(
    listKey: 'documents' | 'bankAccounts' | 'directors' | 'shareholders',
    itemId: number
  ): void {
    if (!this.selectedCompanyProfile?.[listKey]) return;
    const list = this.selectedCompanyProfile[listKey] as any[];
    const newList = list.filter(x => x?.id !== itemId);
    this.selectedCompanyProfile = { ...this.selectedCompanyProfile, [listKey]: newList };
  }

  /** Approve/Reject a single list item (server call + local patch) — humanized toasts */
  reviewItem(
    listKey: 'documents' | 'bankAccounts' | 'directors' | 'shareholders',
    item: any,
    approve: boolean,
    remark?: string
  ) {
    const companyId = this.getCurrentCompanyId();
    if (!companyId || !item?.id) {
      console.warn('[reviewItem] Missing company/item id', { companyId, item });
      return;
    }

    const body = this.buildReviewBody(item.id, approve, remark);
    const entity = this.prettyItem(listKey); // "Document", "Bank account", ...
    // Actions are uppercase in the payload; fall back to lowercase key if needed.
    const action = (item?.actionType ?? item?.actionType ?? 'ADD') as 'ADD' | 'EDIT' | 'DELETE';
    const name = this.itemDisplayName(listKey, item);

    this.dataSubscriber$.add(
      this.postItemReview(listKey, companyId, body)
        ?.pipe(finalize(() => { }))
        .subscribe({
          next: (res: any) => {
            if (res?.success) {
              // If a DELETE is approved, remove it from the list.
              if (approve && action === 'DELETE') {
                this.removeLocalItem(listKey, item.id);
              } else {
                // Otherwise, just patch reviewed/approved state locally.
                this.patchLocalItem(listKey, item.id, {
                  isReviewed: true,
                  isApproved: approve,
                  reviewedOn: this.nowIso(),
                  reviewedBy: this.reviewerIdNumber(),
                  reviewerRemark: remark ?? null
                });
              }

              const { title, message } = this.humanizeDecision(entity, name, action, approve, remark);
              if (approve) {
                this.toaster.success(message, "Success");
              } else {
                this.toaster.success(message, "Success");
              }
            } else {
              const verb = approve ? 'approve' : 'reject';
              this.toaster.error(`Couldn't ${verb} this ${entity.toLowerCase()}. Please try again.`, 'Error');
            }
          },
          error: (err) => {
            console.error('[reviewItem] API error', err);
            const verb = approve ? 'approve' : 'reject';
            this.toaster.error(`We hit a problem while trying to ${verb} this ${entity.toLowerCase()}.`, 'Error');
          }
        })
    );
  }






  private prettySection(k: 'contactDetails' | 'financialSnapshot' | 'authorizedContact' | 'secretary' | 'auditor') {
    switch (k) {
      case 'contactDetails': return 'Contact details';
      case 'financialSnapshot': return 'Financial snapshot';
      case 'authorizedContact': return 'Authorized contact';
      case 'secretary': return 'Secretary';
      case 'auditor': return 'Auditor';
    }
  }

  private prettyItem(k: 'documents' | 'bankAccounts' | 'directors' | 'shareholders') {
    switch (k) {
      case 'documents': return 'Document';
      case 'bankAccounts': return 'Bank account';
      case 'directors': return 'Director';
      case 'shareholders': return 'Shareholder';
    }
  }

  /* ===== Status helpers (unchanged) ===== */
  sectionStatusLabel(s: any | null | undefined): string {
    if (!s) return 'Not provided';
    const action = String(s.actionType || '').toUpperCase();

    // Pending by action
    if (s.isReviewed === false) {
      switch (action) {
        case 'ADD': return 'Pending-Add';
        case 'EDIT': return 'Pending-Edit';
        case 'DELETE': return 'Pending-Delete';
        default: return 'Pending';
      }
    }

    // Final states
    if (s.isReviewed === true && s.isApproved === true) return 'Approved';
    if (s.isReviewed === true && s.isApproved === false) {
      const label = action === 'ADD' ? 'Add'
        : action === 'EDIT' ? 'Edit'
          : action === 'DELETE' ? 'Delete'
            : 'Change';
      return `${label} Rejected`;
    }

    return 'Pending';
  }

  sectionStatusClass(s: any | null | undefined): string {
    if (!s) return 'bg-secondary';
    if (s.isReviewed === false) return 'bg-warning text-dark';
    if (s.isReviewed === true && s.isApproved === true) return 'bg-success';
    if (s.isReviewed === true && s.isApproved === false) return 'bg-danger';
    return 'bg-warning text-dark';
  }
  sectionCardClass(s: any | null | undefined): string {
    if (!s) return 'card-status-none';
    if (s.isReviewed === false) return 'card-status-pending';
    if (s.isReviewed === true && s.isApproved === true) return 'card-status-success';
    if (s.isReviewed === true && s.isApproved === false) return 'card-status-rejected';
    return 'card-status-pending';
  }
  isSectionApproved(s: any | null | undefined): boolean {
    return !!s && s.isReviewed === true && s.isApproved === true;
  }

  /** Items (rows) */
  isItemApproved(i: any | null | undefined): boolean {
    return !!i && i.isReviewed === true && i.isApproved === true;
  }
  itemStatusLabel(i: any | null | undefined): string {
    if (!i) return 'Not provided';
    const action = String(i.actionType || '').toUpperCase();
    // console.log('action', action)
    // Pending states for all three action types
    if (i.isReviewed === false) {
      switch (action) {
        case 'ADD': return 'Pending-Add';
        case 'EDIT': return 'Pending-Edit';
        case 'DELETE': return 'Pending-Delete';
        default: return 'Pending';
      }
    }

    // Approved / Rejected
    if (i.isReviewed === true && i.isApproved === true) return 'Approved';
    if (i.isReviewed === true && i.isApproved === false) {
      const label = action === 'ADD' ? 'Add-'
        : action === 'EDIT' ? 'Edit-'
          : action === 'DELETE' ? 'Delete-'
            : 'Change-';
      return `${label}Rejected`;
    }

    return 'Pending';
  }

  itemStatusClass(i: any | null | undefined): string {
    if (!i) return 'bg-secondary';
    if (i.isReviewed === false) return 'bg-warning text-dark';
    if (i.isReviewed === true && i.isApproved === true) return 'bg-success';
    if (i.isReviewed === true && i.isApproved === false) return 'bg-danger';
    return 'bg-warning text-dark';
  }
  itemRowClass(i: any | null | undefined): string {
    if (!i) return 'row-status-none';
    if (i.isReviewed === false) return 'row-status-pending';
    if (i.isReviewed === true && i.isApproved === true) return 'row-status-success';
    if (i.isReviewed === true && i.isApproved === false) return 'row-status-rejected';
    return 'row-status-pending';
  }

  /** Aggregate list status → drives card header badge & shadow */
  private listAggregateState(list: any[] | null | undefined): 'none' | 'pending' | 'rejected' | 'success' {
    if (!list || list.length === 0) return 'none';
    const anyRejected = list.some(i => i?.isReviewed === true && i?.isApproved === false);
    if (anyRejected) return 'rejected';
    const anyPending = list.some(i => i?.isReviewed === false);
    if (anyPending) return 'pending';
    const allApproved = list.every(i => i?.isReviewed === true && i?.isApproved === true);
    if (allApproved) return 'success';
    return 'pending';
  }
  listStatusLabel(list: any[] | null | undefined): string {
    switch (this.listAggregateState(list)) {
      case 'success': return 'All approved';
      case 'rejected': return 'Some rejected';
      case 'pending': return 'Pending items';
      case 'none': return 'Not provided';
    }
  }
  listStatusClass(list: any[] | null | undefined): string {
    switch (this.listAggregateState(list)) {
      case 'success': return 'bg-success';
      case 'rejected': return 'bg-danger';
      case 'pending': return 'bg-warning text-dark';
      case 'none': return 'bg-secondary';
    }
  }
  listCardClass(list: any[] | null | undefined): string {
    switch (this.listAggregateState(list)) {
      case 'success': return 'card-status-success';
      case 'rejected': return 'card-status-rejected';
      case 'pending': return 'card-status-pending';
      case 'none': return 'card-status-none';
    }
  }

  /* ========= Generic show/hide rejected toggles for multi-item sections ========= */
  showRejected: Record<'documents' | 'bankAccounts' | 'directors' | 'shareholders', boolean> = {
    documents: false,
    bankAccounts: false,
    directors: false,
    shareholders: false
  };

  /** Toggle visibility for rejected items in a given list */
  toggleRejected(listKey: 'documents' | 'bankAccounts' | 'directors' | 'shareholders'): void {
    this.showRejected[listKey] = !this.showRejected[listKey];
  }

  /** Is there at least one rejected item in this list? */
  isAnyRejected(list: any[] | null | undefined): boolean {
    return Array.isArray(list) && list.some(i => i?.isReviewed === true && i?.isApproved === false);
  }

  /** Return the list filtered according to the toggle state (hide rejected by default) */
  filteredList(
    listKey: 'documents' | 'bankAccounts' | 'directors' | 'shareholders',
    list: any[] | null | undefined
  ): any[] {
    if (!Array.isArray(list)) return [];
    if (this.showRejected[listKey]) return list; // show all when toggled ON
    // otherwise hide rejected
    return list.filter(i => !(i?.isReviewed === true && i?.isApproved === false));
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Document preview (modal + iframe)
  // ───────────────────────────────────────────────────────────────
  @ViewChild('docPreviewModal') docPreviewModalRef!: ElementRef;

  previewSafeUrl: SafeResourceUrl | null = null;
  downloadUrl: string | null = null;
  isPreviewLoading = false;
  previewError = '';
  preview = { fileName: '', mimeType: '' };

  private docModalInstance: any | null = null;

  /** Handle click on "View" for a document row */
  viewDocument(companyId: number | null | undefined, doc: any): void {
    const fileName = this.extractFileName(doc);
    if (!companyId || !fileName) {
      this.toaster.warning('Unable to determine document to open.', 'Warning');
      return;
    }
    this.isPreviewLoading = true;
    this.previewError = '';
    this.preview = { fileName, mimeType: '' };
    this.revokePreviewUrls();

    this.dataSubscriber$.add(
      this.companyService.viewProfileDocument(companyId, fileName).subscribe({
        next: (blob: Blob) => {
          this.preview.mimeType = blob?.type || 'application/octet-stream';
          const url = URL.createObjectURL(blob);

          // Check if the file type can be previewed
          if (this.canPreviewInBrowser(this.preview.mimeType)) {
            this.downloadUrl = url;
            this.previewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            this.openDocModal();
          } else {
            // For Excel and other non-previewable files, trigger download with proper filename
            this.downloadFileWithName(blob, fileName);

            // Show user-friendly message based on file type
            const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
            let message = '';

            if (['xlsx', 'xls', 'xlsm'].includes(fileExtension)) {
              message = 'Excel files cannot be previewed in browser. The file has been downloaded to your device.';
            } else if (['docx', 'doc'].includes(fileExtension)) {
              message = 'Word documents cannot be previewed in browser. The file has been downloaded to your device.';
            } else if (['pptx', 'ppt'].includes(fileExtension)) {
              message = 'PowerPoint files cannot be previewed in browser. The file has been downloaded to your device.';
            } else {
              message = `"${fileName}" cannot be previewed in browser. The file has been downloaded to your device.`;
            }

            this.toaster.info(message, 'Download Complete');
            this.isPreviewLoading = false;
          }
        },
        error: () => {
          this.previewError = 'Failed to load document.';
          this.openDocModal();
          this.isPreviewLoading = false;
        },
        complete: () => {
          // Only set loading to false if we opened the modal (preview case)
          // For downloads, we already set it to false above
          if (this.canPreviewInBrowser(this.preview.mimeType)) {
            this.isPreviewLoading = false;
          }
        }
      })
    );
  }

  /** Check if file can be previewed directly in browser */
  private canPreviewInBrowser(mimeType: string): boolean {
    const previewableMimeTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/webp',
      'text/plain',
      'application/msword', // .doc - may not preview well in all browsers
    ];

    return previewableMimeTypes.includes(mimeType.toLowerCase());
  }

  /** Download file with proper filename */
  private downloadFileWithName(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL after a short delay
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  private openDocModal(): void {
    try {
      if (!this.docModalInstance) {
        this.docModalInstance = new bootstrap.Modal(this.docPreviewModalRef.nativeElement, {
          backdrop: 'static',
          keyboard: false
        });
        // Revoke blob URL when modal closes
        this.docPreviewModalRef.nativeElement.addEventListener('hidden.bs.modal', () => {
          this.revokePreviewUrls();
          this.previewError = '';
          this.isPreviewLoading = false;
        });
      }
      this.docModalInstance.show();
    } catch {
      // Fallback if Bootstrap modal isn't available
      if (this.downloadUrl) {
        window.open(this.downloadUrl, '_blank');
      }
    }
    this.resetEmployersPagination();
  }

  private revokePreviewUrls(): void {
    if (this.downloadUrl) {
      URL.revokeObjectURL(this.downloadUrl);
    }
    this.downloadUrl = null;
    this.previewSafeUrl = null;
  }

  private extractFileName(doc: any): string | null {
    // Most likely fields first
    if (doc?.fileName) return String(doc.fileName);
    if (doc?.documentFileName) return String(doc.documentFileName);

    // Try to derive from a fileUrl if present
    //
    const url: string | undefined = doc?.fileUrl;
    if (!url) return null;

    try {
      // If it has a fileName query param (as per your endpoint style)
      const u = new URL(url, window.location.origin);
      const qName = u.searchParams.get('fileName');
      if (qName) return qName;

      // Else last path segment
      const parts = u.pathname.split('/');
      const last = parts[parts.length - 1];
      return last || null;
    } catch {
      // Fallback simple parse
      const qIdx = url.indexOf('?');
      const base = qIdx >= 0 ? url.substring(0, qIdx) : url;
      const slashIdx = base.lastIndexOf('/');
      return slashIdx >= 0 ? base.substring(slashIdx + 1) : base || null;
    }
  }

  // --- Confirm modal state ---
  @ViewChild('confirmOnboardModal') confirmOnboardModalRef!: ElementRef;
  private confirmModalInstance: any | null = null;

  isOnboarding = false;
  onboardPreview = {
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: ''
  };

  // Open confirm modal; prefill preview from selectedCompanyProfile ("p")
  openOnboardConfirm(p: any): void {
    const br = p?.basicRegistration ?? {};
    const ac = p?.authorizedContact ?? {};

    this.onboardPreview = {
      companyName: String(br?.companyName ?? ''),
      firstName: String(ac?.firstName ?? ''),
      lastName: String(ac?.lastName ?? ''),
      email: String(ac?.emailAddress ?? ''),
      mobile: String(ac?.contactNumber01 ?? ac?.contactNumber02 ?? '')
    };

    if (!this.confirmModalInstance) {
      this.confirmModalInstance = new bootstrap.Modal(this.confirmOnboardModalRef.nativeElement, {
        backdrop: 'static',
        keyboard: false
      });
    }
    this.confirmModalInstance.show();
  }

  confirmOnboardSuperAdmin(): void {
    const companyId = this.selectedCompanyProfile?.basicRegistration?.companyId;
    if (!companyId) {
      this.toaster.error('Missing company id.', 'Error');
      return;
    }

    const superAdminPayload = {
      companyId: Number(companyId),
      companyName: String(this.onboardPreview.companyName || ''),
      email: String(this.onboardPreview.email || ''),
      firstName: String(this.onboardPreview.firstName || ''),
      lastName: String(this.onboardPreview.lastName || ''),
      mobileNumber: this.onboardPreview.mobile || undefined
    };

    this.isOnboarding = true;

    this.dataSubscriber$.add(
      this.companyAdminService
        .OnboardCompanySuperAdminAndNotifyAsync(companyId, superAdminPayload)
        .pipe(finalize(() => (this.isOnboarding = false)))
        .subscribe({
          next: (res: any) => {
            if (res?.success) {
              this.toaster.success('Super admin onboarded and credentials emailed.', 'Success');
              this.confirmModalInstance?.hide();
            } else {
              if (res?.message) {
                this.toaster.warning(res?.message, 'Warning');
              }
              else {
                this.toaster.error('Failed to onboard super admin.', 'Error');
              }
            }
          },
          error: (err: unknown) => {
            // console.log(err)
            const msg =
              err && typeof err === 'object' && 'message' in err
                ? String((err as any).message)
                : 'Unknown error';
            console.error('[confirmOnboardSuperAdmin] API error', err);
            this.toaster.error(`API error while onboarding super admin. ${msg}`, 'Error');
          }
        })
    );
  }

  private updateEmployerPage(): void {
    if (this.EmployersPage < 1) this.EmployersPage = 1;
    const start = (this.EmployersPage - 1) * this.EmployersPageSize;
    this.pagedEmployers = this.filteredEmployers.slice(start, start + this.EmployersPageSize);
  }

  private updateOnboardingRequestsPage(): void {
    if (this.OnboardingRequestsPage < 1) this.OnboardingRequestsPage = 1;
    const start = (this.OnboardingRequestsPage - 1) * this.OnboardingRequestsPageSize;
    this.pagedOnboardingRequests = this.filteredOnboardingRequests.slice(start, start + this.OnboardingRequestsPageSize);
  }

  private resetEmployersPagination(): void {
    this.EmployersPage = 1;
    this.updateEmployerPage();
  }

  private resetOnboardingRequestsPagination(): void {
    this.OnboardingRequestsPage = 1;
    this.updateOnboardingRequestsPage();
  }

  // onEmployerPageChange(paginatedData: any[]): void {
  //   this.pagedEmployers = paginatedData;
  // }
  onEmployerPageChange(event: PageEvent): void {
    this.employersCurrentPage = event.page;
    this.employersPageSize = event.pageSize;
    this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId);
  }

  // onOnboardingRequestPageChange(paginatedData: any[]): void {
  //   this.pagedOnboardingRequests = paginatedData;
  // }
  onOnboardingRequestPageChange(event: PageEvent): void {
    this.onboardingCurrentPage = event.page;
    this.onboardingPageSize = event.pageSize;
    this.fetchOnboardingRequests();
  }

  onFilter(result: FilterResult): void {

    const filterValueCompanyId = result['CompanyId']?.toString().trim();
    const filterValueDirectorNic = result['DirectorNic']?.toString().trim();

    if (!filterValueCompanyId && !filterValueDirectorNic) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.employersCurrentPage = 1;
    this.searchText = '';

    this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId, filterValueCompanyId, filterValueDirectorNic);
  }

  onClear(): void {
    this.employersCurrentPage = 1;
    this.searchText = '';
    this.empError = '';
    this.fetchEmployers(this.employersCurrentPage, this.employersPageSize, this.filterEmployerTypeId);
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ EWW Block
  // ───────────────────────────────────────────────────────────────

  onEWWBlockToggle(): void {
    if (this.isEwwBlocked) {
      // Currently blocked → show unblock confirmation
      if (!this.ewwUnblockModalInstance) {
        this.ewwUnblockModalInstance = new bootstrap.Modal(this.ewwUnblockModalRef.nativeElement, {
          backdrop: 'static', keyboard: false
        });
      }
      this.ewwUnblockModalInstance.show();
    } else {
      // Currently active → show block modal with reason
      this.ewwBlockReason = '';
      if (!this.ewwBlockModalInstance) {
        this.ewwBlockModalInstance = new bootstrap.Modal(this.ewwBlockModalRef.nativeElement, {
          backdrop: 'static', keyboard: false
        });
      }
      this.ewwBlockModalInstance.show();
    }
  }

  cancelEWWBlock(): void {
    this.ewwBlockReason = '';
    this.ewwBlockModalInstance?.hide();
  }

  confirmEWWBlock(): void {
    if (!this.ewwBlockReason.trim()) {
      this.toaster.warning('Please enter a block reason.', 'Required');
      return;
    }
    const companyId = this.selectedCompanyProfile?.basicRegistration?.companyId;
    if (!companyId) return;

    this.isEwwBlockSaving = true;
    this.dataSubscriber$.add(
      this.companyService.saveCompanyEWWBlock({
        companyId,
        isBlocked: true,
        blockReason: this.ewwBlockReason.trim()
      }).subscribe({
        next: (res) => {
          if (res.success) {
            this.isEwwBlocked = true;
            if (this.selectedCompanyProfile?.basicRegistration) {
              this.selectedCompanyProfile.basicRegistration.isBlockEWW = true;
            }
            this.ewwBlockReason = '';
            this.ewwBlockModalInstance?.hide();
            this.toaster.success('EWW access has been blocked successfully.', 'Blocked');
          } else {
            this.toaster.error(res.message || 'Failed to block EWW access.', 'Error');
          }
        },
        error: () => {
          this.toaster.error('API error while blocking EWW access.', 'Error');
        },
        complete: () => {
          this.isEwwBlockSaving = false;
        }
      })
    );
  }

  cancelEWWUnblock(): void {
    this.ewwUnblockModalInstance?.hide();
  }

  confirmEWWUnblock(): void {
    const companyId = this.selectedCompanyProfile?.basicRegistration?.companyId;
    if (!companyId) return;

    this.isEwwBlockSaving = true;
    this.dataSubscriber$.add(
      this.companyService.saveCompanyEWWBlock({
        companyId,
        isBlocked: false,
        blockReason: null
      }).subscribe({
        next: (res) => {
          if (res.success) {
            this.isEwwBlocked = false;
            if (this.selectedCompanyProfile?.basicRegistration) {
              this.selectedCompanyProfile.basicRegistration.isBlockEWW = false;
            }
            this.ewwUnblockModalInstance?.hide();
            this.toaster.success('EWW access has been unblocked successfully.', 'Unblocked');
          } else {
            this.toaster.error(res.message || 'Failed to unblock EWW access.', 'Error');
          }
        },
        error: () => {
          this.toaster.error('API error while unblocking EWW access.', 'Error');
        },
        complete: () => {
          this.isEwwBlockSaving = false;
        }
      })
    );
  }

}
